// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { useRouter } from 'vue-router';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------

import { useDialogStore } from '@/store/dialog.store';
import { useHorraireCommerceStore } from '@/store/horraire.commerce.store';
import { HorraireCommerceService } from '@/services/horraire.commerce.service';
import type {
  IHorraireCommerceBody,
  INewScheduleItem,
  IWeekdayScheduleBody,
} from '@/types/horraire.commerce.types';
import { isAtcivatedHorraireCommerceWatch } from '@/refs/horraire.commerce.refs';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------

/**
 * Hook pour gérer les mutations liées au HorraireCommerce.
 * Fournit des fonctions pour créer un HorraireCommerce, ajouter contacts, logos, et supprimer un HorraireCommerce.
 * Gère également les étapes du formulaire et les erreurs globales.
 */
export const HorraireCommerceMutation = () => {
  const router = useRouter();
  const HorraireCommerceStore = useHorraireCommerceStore();
  const dialogStore = useDialogStore();

  // -------------------------------
  // 🔹 Gestion des erreurs globale
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0];

    isGlobalError.value = true;
    isGlobalError.value = true;
    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    isAtcivatedHorraireCommerceWatch.value =
      !isAtcivatedHorraireCommerceWatch.value;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 1500);
  };

  // -------------------------------
  // 🔹 Création d'un HorraireCommerce
  // -------------------------------
  const createHorraireCommerce = useMutation<any, Error, IHorraireCommerceBody>(
    {
      mutationFn: (payload: any) =>
        HorraireCommerceStore.createHorraireCommerce(payload),
      onSuccess: () => {
        isAtcivatedHorraireCommerceWatch.value =
          !isAtcivatedHorraireCommerceWatch.value;
      },
      onError: (err) => handleError(err),
    },
  );

  // -------------------------------
  // 🔹 Mise à jour d'un HorraireCommerce par ID
  // -------------------------------
  const updateHorraireCommerce = useMutation<any, Error, INewScheduleItem>({
    mutationFn: (payload: INewScheduleItem) => {
      return HorraireCommerceStore.updateHorraireCommerce(payload);
    },
    onSuccess: async (data) => {
      // Mettre à jour le store ou notifier l'utilisateur
    },
    onError: (err) => {
      const error = err as AxiosError;
      const response = error.response?.data as any;

      const message = response?.message[0];
      isGlobalError.value = true;
      GlobalErrorMessage.value = message;
      setTimeout(() => {
        isGlobalError.value = false;
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
  });

  // -------------------------------
  // 🔹 Suppression d'un HorraireCommerce
  // -------------------------------
  const DeleteHorraireCommerce = useMutation<any, Error>({
    mutationFn: () => HorraireCommerceStore.deleteHorraireCommerce(),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  const handleCreateHorraireCommerce = (item: IHorraireCommerceBody) => {
    return createHorraireCommerce.mutateAsync(item);
  };

  const handleUpdateHorraireCommerce = (item: INewScheduleItem) => {
    return updateHorraireCommerce.mutateAsync(item);
  };
  const handleDeleteHorraireCommerce = () =>
    DeleteHorraireCommerce.mutateAsync();

  const handleUpdateWeekdaySchedule = (
    storeId: string,
    data: IWeekdayScheduleBody,
  ) => HorraireCommerceService.updateWeekdaySchedule(storeId, data);

  return {
    handleCreateHorraireCommerce,
    handleUpdateHorraireCommerce,
    handleUpdateWeekdaySchedule,
    handleDeleteHorraireCommerce,
  };
};
